import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Image,
  Loader,
  Paper,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { IconDeviceFloppy, IconPhotoUp, IconPlus, IconTrash } from "@tabler/icons-react";
import { Colors, HttpSuccessCodesObj } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import { DefaultErrorNotification, DefaultSuccessNotification } from "../../Lib/methods";
import { NonPaginatedResponse } from "../../Lib/responses";
import {
  SiteContentDocumentProps,
  SiteContentLinkProps,
  SiteLandingPageLocaleProps,
} from "../../Lib/types";
import { PerformRequest, UploadFiles, usePerformRequest } from "../../Lib/usePerformRequest";

const localeOptions = [
  { label: "English", value: "en" },
  { label: "Portuguese", value: "pt" },
  { label: "French", value: "fr" },
  { label: "German", value: "de" },
];

const field = (
  label: string,
  value: string,
  onChange: (value: string) => void,
  multiline?: boolean
) =>
  multiline ? (
    <Textarea
      label={label}
      value={value || ""}
      onChange={(event) => onChange(event.currentTarget.value)}
      minRows={3}
      autosize
    />
  ) : (
    <TextInput
      label={label}
      value={value || ""}
      onChange={(event) => onChange(event.currentTarget.value)}
    />
  );

const PageEditor: React.FC = () => {
  const { data, isLoading, reloadData } = usePerformRequest<
    NonPaginatedResponse<SiteContentDocumentProps>
  >({
    method: Endpoints.GetSiteContent().method,
    route: Endpoints.GetSiteContent().route,
  });

  const [siteContent, setSiteContent] = useState<SiteContentDocumentProps | null>(null);
  const [activeLocale, setActiveLocale] = useState("en");
  const [isSavingBranding, setSavingBranding] = useState(false);
  const [isSavingLandingPage, setSavingLandingPage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadTargetRef = useRef<((url: string) => Promise<void> | void) | null>(null);

  useEffect(() => {
    if (data?.data) {
      setSiteContent(data.data);
    }
  }, [data]);

  const localeContent = useMemo<SiteLandingPageLocaleProps | null>(() => {
    if (!siteContent) {
      return null;
    }
    return siteContent.landingPage?.[activeLocale] || siteContent.landingPage?.en || null;
  }, [siteContent, activeLocale]);

  const updateBranding = (fieldName: "siteName" | "primaryLogo", value: string) => {
    setSiteContent((current) =>
      current
        ? {
            ...current,
            branding: {
              ...current.branding,
              [fieldName]: value,
            },
          }
        : current
    );
  };

  const updateLocaleSection = (
    section: keyof SiteLandingPageLocaleProps,
    updater: (sectionData: any) => any
  ) => {
    setSiteContent((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        landingPage: {
          ...current.landingPage,
          [activeLocale]: {
            ...current.landingPage[activeLocale],
            [section]: updater(current.landingPage[activeLocale][section]),
          },
        },
      };
    });
  };

  const openUploader = (setter: (url: string) => Promise<void> | void) => {
    uploadTargetRef.current = setter;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (file: File | null) => {
    if (!file || !uploadTargetRef.current) {
      return;
    }
    try {
      const uploaded = await UploadFiles([file], false, "r2");
      const path = uploaded?.data?.[0]?.path;
      if (!path) {
        DefaultErrorNotification("Upload failed");
        return;
      }
      await uploadTargetRef.current(path);
      DefaultSuccessNotification("Upload completed");
    } catch {
      DefaultErrorNotification("Upload failed");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      uploadTargetRef.current = null;
    }
  };

  const saveBranding = async () => {
    if (!siteContent) {
      return;
    }
    setSavingBranding(true);
    const response = await PerformRequest<NonPaginatedResponse<SiteContentDocumentProps>>({
      method: Endpoints.GetUpdateSiteBranding().method,
      route: Endpoints.GetUpdateSiteBranding().route,
      data: siteContent.branding,
      callback: () => {
        setSavingBranding(false);
      },
    });
    setSavingBranding(false);
    if (response?.status === HttpSuccessCodesObj.Success) {
      DefaultSuccessNotification("Branding updated");
      reloadData();
      return;
    }
    DefaultErrorNotification(response?.data?.message || "Could not update branding");
  };

  const saveBrandingPayload = async (branding: SiteContentDocumentProps["branding"]) => {
    setSavingBranding(true);
    const response = await PerformRequest<NonPaginatedResponse<SiteContentDocumentProps>>({
      method: Endpoints.GetUpdateSiteBranding().method,
      route: Endpoints.GetUpdateSiteBranding().route,
      data: branding,
      callback: () => {
        setSavingBranding(false);
      },
    });
    setSavingBranding(false);
    return response;
  };

  const saveLandingPage = async () => {
    if (!siteContent) {
      return;
    }
    setSavingLandingPage(true);
    const response = await PerformRequest<NonPaginatedResponse<SiteContentDocumentProps>>({
      method: Endpoints.GetUpdateLandingPageContent().method,
      route: Endpoints.GetUpdateLandingPageContent().route,
      data: {
        landingPage: siteContent.landingPage,
      },
      callback: () => {
        setSavingLandingPage(false);
      },
    });
    setSavingLandingPage(false);
    if (response?.status === HttpSuccessCodesObj.Success) {
      DefaultSuccessNotification("Landing page updated");
      reloadData();
      return;
    }
    DefaultErrorNotification(response?.data?.message || "Could not update landing page");
  };

  const saveLandingPagePayload = async (landingPage: SiteContentDocumentProps["landingPage"]) => {
    setSavingLandingPage(true);
    const response = await PerformRequest<NonPaginatedResponse<SiteContentDocumentProps>>({
      method: Endpoints.GetUpdateLandingPageContent().method,
      route: Endpoints.GetUpdateLandingPageContent().route,
      data: {
        landingPage,
      },
      callback: () => {
        setSavingLandingPage(false);
      },
    });
    setSavingLandingPage(false);
    return response;
  };

  const applyBrandingUpload = async (fieldName: "primaryLogo", value: string) => {
    if (!siteContent) {
      return;
    }
    const nextBranding = {
      ...siteContent.branding,
      [fieldName]: value,
    };
    setSiteContent((current) =>
      current
        ? {
            ...current,
            branding: nextBranding,
          }
        : current
    );
    const response = await saveBrandingPayload(nextBranding);
    if (response?.status !== HttpSuccessCodesObj.Success) {
      DefaultErrorNotification(response?.data?.message || "Could not update branding");
      return;
    }
    reloadData();
  };

  const applyLandingPageUpload = async (
    updater: (landingPage: SiteContentDocumentProps["landingPage"]) => SiteContentDocumentProps["landingPage"]
  ) => {
    if (!siteContent) {
      return;
    }
    const nextLandingPage = updater(siteContent.landingPage);
    setSiteContent((current) =>
      current
        ? {
            ...current,
            landingPage: nextLandingPage,
          }
        : current
    );
    const response = await saveLandingPagePayload(nextLandingPage);
    if (response?.status !== HttpSuccessCodesObj.Success) {
      DefaultErrorNotification(response?.data?.message || "Could not update landing page");
      return;
    }
    reloadData();
  };

  const renderListEditor = ({
    label,
    items,
    onChange,
    fields,
    createItem,
    onImageUpload,
  }: {
    label: string;
    items: Record<string, string>[];
    onChange: (items: Record<string, string>[]) => void;
    fields: { key: string; label: string; multiline?: boolean; isImage?: boolean }[];
    createItem: () => Record<string, string>;
    onImageUpload?: (index: number, key: string, url: string) => Promise<void> | void;
  }) => (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={700}>{label}</Text>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => onChange([...(items || []), createItem()])}
        >
          Add item
        </Button>
      </Group>
      {(items || []).map((item, index) => (
        <Card key={`${label}-${index}`} withBorder radius="lg" p="md">
          <Stack gap="sm">
            <Group justify="space-between">
              <Badge variant="light">Item {index + 1}</Badge>
              <ActionIcon
                color="red"
                variant="subtle"
                onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
            {fields.map((itemField) => (
              <Box key={itemField.key}>
                {field(
                  itemField.label,
                  item[itemField.key] || "",
                  (value) => {
                    const nextItems = [...items];
                    nextItems[index] = {
                      ...nextItems[index],
                      [itemField.key]: value,
                    };
                    onChange(nextItems);
                  },
                  itemField.multiline
                )}
                {itemField.isImage && (
                  <Group justify="flex-end" mt={6}>
                    <Button
                      variant="subtle"
                      size="xs"
                      leftSection={<IconPhotoUp size={14} />}
                      onClick={() =>
                        openUploader((url) => {
                          if (onImageUpload) {
                            return onImageUpload(index, itemField.key, url);
                          }
                          const nextItems = [...items];
                          nextItems[index] = {
                            ...nextItems[index],
                            [itemField.key]: url,
                          };
                          onChange(nextItems);
                        })
                      }
                    >
                      Upload
                    </Button>
                  </Group>
                )}
              </Box>
            ))}
          </Stack>
        </Card>
      ))}
    </Stack>
  );

  if (isLoading || !siteContent || !localeContent) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(event) => handleFileSelected(event.currentTarget.files?.[0] || null)}
      />

      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2}>Page Editor</Title>
            <Text c="dimmed" mt={6}>
              Manage student-public branding and fixed landing-page sections.
            </Text>
          </div>
          <Button variant="light" color={Colors.PurplePrimary} onClick={reloadData}>
            Reload
          </Button>
        </Group>

        <Tabs defaultValue="branding" radius="lg">
          <Tabs.List>
            <Tabs.Tab value="branding">Branding</Tabs.Tab>
            <Tabs.Tab value="landing">Landing Page</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="branding" pt="lg">
            <Grid gutter="xl">
              <Grid.Col span={{ base: 12, lg: 7 }}>
                <Paper withBorder radius="xl" p="xl">
                  <Stack gap="lg">
                    <Title order={4}>Branding</Title>
                    <TextInput
                      label="Website name"
                      value={siteContent.branding.siteName || ""}
                      onChange={(event) => updateBranding("siteName", event.currentTarget.value)}
                    />
                    <TextInput
                      label="Primary logo URL"
                      value={siteContent.branding.primaryLogo || ""}
                      onChange={(event) =>
                        updateBranding("primaryLogo", event.currentTarget.value)
                      }
                    />
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">
                        Upload a logo image or paste a direct image URL.
                      </Text>
                      <Button
                        variant="light"
                        leftSection={<IconPhotoUp size={16} />}
                        onClick={() => openUploader((url) => applyBrandingUpload("primaryLogo", url))}
                      >
                        Upload logo
                      </Button>
                    </Group>
                    <Button
                      leftSection={<IconDeviceFloppy size={16} />}
                      onClick={saveBranding}
                      loading={isSavingBranding}
                    >
                      Save branding
                    </Button>
                  </Stack>
                </Paper>
              </Grid.Col>
              <Grid.Col span={{ base: 12, lg: 5 }}>
                <Paper withBorder radius="xl" p="xl">
                  <Stack gap="lg">
                    <Title order={4}>Preview</Title>
                    <Card radius="xl" withBorder p="xl">
                      <Stack gap="md" align="center">
                        <Image
                          src={siteContent.branding.primaryLogo}
                          alt={siteContent.branding.siteName}
                          h={72}
                          fit="contain"
                        />
                        <Title order={3}>{siteContent.branding.siteName}</Title>
                      </Stack>
                    </Card>
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="landing" pt="lg">
            <Stack gap="lg">
              <Group justify="space-between" align="flex-end">
                <Select
                  label="Language"
                  data={localeOptions}
                  value={activeLocale}
                  onChange={(value) => setActiveLocale(value || "en")}
                  w={220}
                />
                <Button
                  leftSection={<IconDeviceFloppy size={16} />}
                  onClick={saveLandingPage}
                  loading={isSavingLandingPage}
                >
                  Save landing page
                </Button>
              </Group>

              <Alert radius="xl" color="blue" variant="light">
                Section order is fixed. You can edit content and toggle visibility.
              </Alert>

              <Accordion variant="contained" radius="lg">
                <Accordion.Item value="hero">
                  <Accordion.Control>Hero</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show hero section"
                        checked={localeContent.hero.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("hero", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {field("Badge text", localeContent.hero.badgeText, (value) =>
                        updateLocaleSection("hero", (section) => ({ ...section, badgeText: value }))
                      )}
                      {field("Title", localeContent.hero.title, (value) =>
                        updateLocaleSection("hero", (section) => ({ ...section, title: value }))
                      )}
                      {field(
                        "Highlighted title",
                        localeContent.hero.highlightedTitle,
                        (value) =>
                          updateLocaleSection("hero", (section) => ({
                            ...section,
                            highlightedTitle: value,
                          }))
                      )}
                      {field("Description", localeContent.hero.description, (value) =>
                        updateLocaleSection("hero", (section) => ({
                          ...section,
                          description: value,
                        })),
                        true
                      )}
                      {field("CTA label", localeContent.hero.ctaLabel, (value) =>
                        updateLocaleSection("hero", (section) => ({ ...section, ctaLabel: value }))
                      )}
                      {field("Hero image URL", localeContent.hero.heroImage, (value) =>
                        updateLocaleSection("hero", (section) => ({ ...section, heroImage: value }))
                      )}
                      <Group justify="space-between">
                        <Button
                          variant="light"
                          leftSection={<IconPhotoUp size={16} />}
                          onClick={() =>
                            openUploader((url) =>
                              applyLandingPageUpload((landingPage) => ({
                                ...landingPage,
                                [activeLocale]: {
                                  ...landingPage[activeLocale],
                                  hero: {
                                    ...landingPage[activeLocale].hero,
                                    heroImage: url,
                                  },
                                },
                              }))
                            )
                          }
                        >
                          Upload hero image
                        </Button>
                        <Image src={localeContent.hero.heroImage} h={72} w={120} fit="cover" />
                      </Group>
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="stats">
                  <Accordion.Control>Stats</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show stats section"
                        checked={localeContent.stats.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("stats", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {renderListEditor({
                        label: "Stat items",
                        items: localeContent.stats.items as any,
                        fields: [
                          { key: "label", label: "Label" },
                          { key: "value", label: "Value" },
                        ],
                        createItem: () => ({ label: "", value: "" }),
                        onChange: (items) =>
                          updateLocaleSection("stats", (section) => ({ ...section, items })),
                      })}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="features">
                  <Accordion.Control>Features</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show features section"
                        checked={localeContent.features.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("features", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {field("Section title", localeContent.features.title, (value) =>
                        updateLocaleSection("features", (section) => ({ ...section, title: value }))
                      )}
                      {field(
                        "Section subtitle",
                        localeContent.features.subtitle,
                        (value) =>
                          updateLocaleSection("features", (section) => ({
                            ...section,
                            subtitle: value,
                          })),
                        true
                      )}
                      {renderListEditor({
                        label: "Feature cards",
                        items: localeContent.features.items as any,
                        fields: [
                          { key: "title", label: "Title" },
                          { key: "description", label: "Description", multiline: true },
                        ],
                        createItem: () => ({ title: "", description: "" }),
                        onChange: (items) =>
                          updateLocaleSection("features", (section) => ({ ...section, items })),
                      })}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="promo">
                  <Accordion.Control>Promo</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show promo section"
                        checked={localeContent.promo.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("promo", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {field("Title", localeContent.promo.title, (value) =>
                        updateLocaleSection("promo", (section) => ({ ...section, title: value }))
                      )}
                      {field("Body", localeContent.promo.body, (value) =>
                        updateLocaleSection("promo", (section) => ({ ...section, body: value })),
                        true
                      )}
                      {field("CTA label", localeContent.promo.ctaLabel, (value) =>
                        updateLocaleSection("promo", (section) => ({ ...section, ctaLabel: value }))
                      )}
                      {field("Price text", localeContent.promo.priceText, (value) =>
                        updateLocaleSection("promo", (section) => ({ ...section, priceText: value }))
                      )}
                      {field("Image URL", localeContent.promo.image, (value) =>
                        updateLocaleSection("promo", (section) => ({ ...section, image: value }))
                      )}
                      <Group justify="space-between">
                        <Button
                          variant="light"
                          leftSection={<IconPhotoUp size={16} />}
                          onClick={() =>
                            openUploader((url) =>
                              applyLandingPageUpload((landingPage) => ({
                                ...landingPage,
                                [activeLocale]: {
                                  ...landingPage[activeLocale],
                                  promo: {
                                    ...landingPage[activeLocale].promo,
                                    image: url,
                                  },
                                },
                              }))
                            )
                          }
                        >
                          Upload promo image
                        </Button>
                        <Image src={localeContent.promo.image} h={72} w={120} fit="cover" />
                      </Group>
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="about">
                  <Accordion.Control>About</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show about section"
                        checked={localeContent.about.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("about", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {[
                        ["title", "Section title", false],
                        ["subtitle", "Section subtitle", true],
                        ["visionTitle", "Vision title", false],
                        ["vision", "Vision", true],
                        ["missionTitle", "Mission title", false],
                        ["mission", "Mission", true],
                        ["historyTitle", "History title", false],
                        ["history", "History", true],
                        ["valuesTitle", "Values title", false],
                        ["values", "Values", true],
                      ].map(([key, label, multiline]) => {
                        const fieldKey = String(key);
                        return (
                          <Box key={fieldKey}>
                            {field(
                              label as string,
                              (localeContent.about as any)[fieldKey] || "",
                              (value) =>
                                updateLocaleSection("about", (section) => ({
                                  ...section,
                                  [fieldKey]: value,
                                })),
                              Boolean(multiline)
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="how-it-works">
                  <Accordion.Control>How It Works</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show how it works section"
                        checked={localeContent.howItWorks.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("howItWorks", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {field("Section title", localeContent.howItWorks.title, (value) =>
                        updateLocaleSection("howItWorks", (section) => ({
                          ...section,
                          title: value,
                        }))
                      )}
                      {field(
                        "Section subtitle",
                        localeContent.howItWorks.subtitle,
                        (value) =>
                          updateLocaleSection("howItWorks", (section) => ({
                            ...section,
                            subtitle: value,
                          })),
                        true
                      )}
                      {renderListEditor({
                        label: "Steps",
                        items: localeContent.howItWorks.steps as any,
                        fields: [
                          { key: "title", label: "Title" },
                          { key: "description", label: "Description", multiline: true },
                        ],
                        createItem: () => ({ title: "", description: "" }),
                        onChange: (items) =>
                          updateLocaleSection("howItWorks", (section) => ({ ...section, steps: items })),
                      })}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="testimonials">
                  <Accordion.Control>Testimonials</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show testimonials section"
                        checked={localeContent.testimonials.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("testimonials", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {field("Section title", localeContent.testimonials.title, (value) =>
                        updateLocaleSection("testimonials", (section) => ({
                          ...section,
                          title: value,
                        }))
                      )}
                      {field(
                        "Section subtitle",
                        localeContent.testimonials.subtitle,
                        (value) =>
                          updateLocaleSection("testimonials", (section) => ({
                            ...section,
                            subtitle: value,
                          })),
                        true
                      )}
                      {renderListEditor({
                        label: "Testimonials",
                        items: localeContent.testimonials.items as any,
                        fields: [
                          { key: "name", label: "Name" },
                          { key: "role", label: "Role" },
                          { key: "text", label: "Text", multiline: true },
                          { key: "avatar", label: "Avatar URL", isImage: true },
                        ],
                        createItem: () => ({ name: "", role: "", text: "", avatar: "" }),
                        onImageUpload: (index, key, url) =>
                          applyLandingPageUpload((landingPage) => {
                            const nextItems = [...landingPage[activeLocale].testimonials.items];
                            nextItems[index] = {
                              ...nextItems[index],
                              [key]: url,
                            };
                            return {
                              ...landingPage,
                              [activeLocale]: {
                                ...landingPage[activeLocale],
                                testimonials: {
                                  ...landingPage[activeLocale].testimonials,
                                  items: nextItems,
                                },
                              },
                            };
                          }),
                        onChange: (items) =>
                          updateLocaleSection("testimonials", (section) => ({ ...section, items })),
                      })}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="teachers">
                  <Accordion.Control>Teachers</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show teachers section"
                        checked={localeContent.teachers.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("teachers", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {field("Section title", localeContent.teachers.title, (value) =>
                        updateLocaleSection("teachers", (section) => ({
                          ...section,
                          title: value,
                        }))
                      )}
                      {field(
                        "Section subtitle",
                        localeContent.teachers.subtitle,
                        (value) =>
                          updateLocaleSection("teachers", (section) => ({
                            ...section,
                            subtitle: value,
                          })),
                        true
                      )}
                      {renderListEditor({
                        label: "Teacher profiles",
                        items: localeContent.teachers.items as any,
                        fields: [
                          { key: "name", label: "Name" },
                          { key: "title", label: "Title" },
                          { key: "description", label: "Description", multiline: true },
                          { key: "avatar", label: "Avatar URL", isImage: true },
                        ],
                        createItem: () => ({ name: "", title: "", description: "", avatar: "" }),
                        onImageUpload: (index, key, url) =>
                          applyLandingPageUpload((landingPage) => {
                            const nextItems = [...landingPage[activeLocale].teachers.items];
                            nextItems[index] = {
                              ...nextItems[index],
                              [key]: url,
                            };
                            return {
                              ...landingPage,
                              [activeLocale]: {
                                ...landingPage[activeLocale],
                                teachers: {
                                  ...landingPage[activeLocale].teachers,
                                  items: nextItems,
                                },
                              },
                            };
                          }),
                        onChange: (items) =>
                          updateLocaleSection("teachers", (section) => ({ ...section, items })),
                      })}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="courses-offered">
                  <Accordion.Control>Courses Offered</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show courses offered section"
                        checked={localeContent.coursesOffered.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("coursesOffered", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {field("Section title", localeContent.coursesOffered.title, (value) =>
                        updateLocaleSection("coursesOffered", (section) => ({
                          ...section,
                          title: value,
                        }))
                      )}
                      {field(
                        "Section subtitle",
                        localeContent.coursesOffered.subtitle,
                        (value) =>
                          updateLocaleSection("coursesOffered", (section) => ({
                            ...section,
                            subtitle: value,
                          })),
                        true
                      )}
                      {renderListEditor({
                        label: "Course cards",
                        items: localeContent.coursesOffered.items as any,
                        fields: [
                          { key: "title", label: "Title" },
                          { key: "description", label: "Description", multiline: true },
                        ],
                        createItem: () => ({ title: "", description: "" }),
                        onChange: (items) =>
                          updateLocaleSection("coursesOffered", (section) => ({
                            ...section,
                            items,
                          })),
                      })}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="footer">
                  <Accordion.Control>Footer</Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      <Switch
                        label="Show footer section"
                        checked={localeContent.footer.isEnabled}
                        onChange={(event) =>
                          updateLocaleSection("footer", (section) => ({
                            ...section,
                            isEnabled: event.currentTarget.checked,
                          }))
                        }
                      />
                      {[
                        ["tagline", "Tagline", true],
                        ["phone", "Phone", false],
                        ["email", "Email", false],
                        ["nigeriaLocation", "Nigeria location", true],
                        ["ukLocation", "UK location", true],
                      ].map(([key, label, multiline]) => {
                        const fieldKey = String(key);
                        return (
                          <Box key={fieldKey}>
                            {field(
                              label as string,
                              (localeContent.footer as any)[fieldKey] || "",
                              (value) =>
                                updateLocaleSection("footer", (section) => ({
                                  ...section,
                                  [fieldKey]: value,
                                })),
                              Boolean(multiline)
                            )}
                          </Box>
                        );
                      })}
                      <Divider />
                      {renderListEditor({
                        label: "Company links",
                        items: localeContent.footer.companyLinks as SiteContentLinkProps[] as any,
                        fields: [
                          { key: "label", label: "Label" },
                          { key: "href", label: "Href" },
                        ],
                        createItem: () => ({ label: "", href: "" }),
                        onChange: (items) =>
                          updateLocaleSection("footer", (section) => ({
                            ...section,
                            companyLinks: items,
                          })),
                      })}
                      {renderListEditor({
                        label: "Learning links",
                        items: localeContent.footer.learningLinks as SiteContentLinkProps[] as any,
                        fields: [
                          { key: "label", label: "Label" },
                          { key: "href", label: "Href" },
                        ],
                        createItem: () => ({ label: "", href: "" }),
                        onChange: (items) =>
                          updateLocaleSection("footer", (section) => ({
                            ...section,
                            learningLinks: items,
                          })),
                      })}
                      {renderListEditor({
                        label: "Support links",
                        items: localeContent.footer.supportLinks as SiteContentLinkProps[] as any,
                        fields: [
                          { key: "label", label: "Label" },
                          { key: "href", label: "Href" },
                        ],
                        createItem: () => ({ label: "", href: "" }),
                        onChange: (items) =>
                          updateLocaleSection("footer", (section) => ({
                            ...section,
                            supportLinks: items,
                          })),
                      })}
                      {renderListEditor({
                        label: "Social links",
                        items: localeContent.footer.socialLinks as SiteContentLinkProps[] as any,
                        fields: [
                          { key: "label", label: "Label" },
                          { key: "href", label: "Href" },
                        ],
                        createItem: () => ({ label: "", href: "" }),
                        onChange: (items) =>
                          updateLocaleSection("footer", (section) => ({
                            ...section,
                            socialLinks: items,
                          })),
                      })}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
};

export default PageEditor;
